/**
 * ModuleSwitcher
 * 
 * Global app switcher displayed in the Navbar.
 * Allows users to quickly jump between different Axelio modules.
 * Shows upsell modal for modules the user doesn't have access to.
 */

import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LayoutGrid, LineChart, Package, Users, FileScan, Cable, ChevronRight, Lock } from 'lucide-react';
import { usePermissions } from '@/hooks/usePermissions';
import { ModuleUpsellModal } from '@/components/ui/ModuleUpsellModal';

interface Module {
  id: string;
  name: string;
  nameHe: string;
  icon: React.ReactNode;
  path: string;
  requiredModule: 'cashflow' | 'inventory' | 'hr' | 'invoice_intake' | 'interfaces';
}

const MODULES: Module[] = [
  {
    id: 'cashflow',
    name: 'Cash Flow',
    nameHe: 'תזרים מזומנים',
    icon: <LineChart className="w-5 h-5" />,
    path: '/app/cashflow',
    requiredModule: 'cashflow',
  },
  {
    id: 'inventory',
    name: 'Inventory',
    nameHe: 'ניהול מלאי',
    icon: <Package className="w-5 h-5" />,
    path: '/app/inventory',
    requiredModule: 'inventory',
  },
  {
    id: 'hr',
    name: 'HR / Employees',
    nameHe: 'ניהול עובדים',
    icon: <Users className="w-5 h-5" />,
    path: '/app/hr',
    requiredModule: 'hr',
  },
  {
    id: 'invoice_intake',
    name: 'Invoice Intake',
    nameHe: 'קליטת חשבוניות',
    icon: <FileScan className="w-5 h-5" />,
    path: '/app/invoice-intake',
    requiredModule: 'invoice_intake',
  },
  {
    id: 'interfaces',
    name: 'Interfaces',
    nameHe: 'ממשקים',
    icon: <Cable className="w-5 h-5" />,
    path: '/app/interfaces',
    requiredModule: 'interfaces',
  },
];

export function ModuleSwitcher() {
  const navigate = useNavigate();
  const location = useLocation();
  const { moduleActive, loadingModules } = usePermissions();
  const [isOpen, setIsOpen] = useState(false);
  const [upsellModule, setUpsellModule] = useState<Module | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Determine current module based on path
  const currentModule = MODULES.find(m => location.pathname.startsWith(m.path));

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleModuleClick = (module: Module) => {
    setIsOpen(false);

    // Check if user has access to this module
    const hasAccess = moduleActive(module.requiredModule);

    if (hasAccess) {
      // User has access - navigate to the module
      navigate(module.path);
    } else {
      // User doesn't have access - show upsell modal
      setUpsellModule(module);
    }
  };

  const closeUpsellModal = () => {
    setUpsellModule(null);
  };

  if (loadingModules) {
    return (
      <div className="p-2">
        <div className="w-10 h-10 rounded-lg bg-gray-100 animate-pulse" />
      </div>
    );
  }

  return (
    <>
      <div className="relative" ref={dropdownRef}>
        {/* Switcher Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`
            p-2.5 rounded-lg transition-all duration-200
            ${
              isOpen
                ? 'bg-primary-50 text-primary-700 ring-2 ring-primary-200'
                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            }
          `}
          aria-label="Module Switcher"
          title="Switch Module"
        >
          <LayoutGrid className="w-5 h-5" />
        </button>

        {/* Dropdown Menu */}
        {isOpen && (
          <div className="absolute ltr:left-0 rtl:right-0 ltr:origin-top-left rtl:origin-top-right mt-2 w-72 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-200">
            {/* Header */}
            <div className="px-4 py-3 border-b border-gray-200 bg-gradient-to-br from-gray-50 to-white">
              <h3 className="text-sm font-semibold text-gray-900 text-right">
                בחר מודול
              </h3>
              <p className="text-xs text-gray-600 text-right mt-0.5">
                עבור בין המערכות של Axelio
              </p>
            </div>

            {/* Module List */}
            <div className="py-2">
              {MODULES.map((module) => {
                const hasAccess = moduleActive(module.requiredModule);
                const isActive = currentModule?.id === module.id;

                return (
                  <button
                    key={module.id}
                    onClick={() => handleModuleClick(module)}
                    className={`
                      w-full px-4 py-3 flex items-center justify-between gap-3
                      transition-all duration-200 text-right
                      ${
                        isActive
                          ? 'bg-primary-50 border-r-4 border-primary-600'
                          : 'hover:bg-gray-50'
                      }
                      ${!hasAccess ? 'opacity-75' : ''}
                    `}
                  >
                    {/* Module Info */}
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div
                        className={`
                          p-2 rounded-lg flex-shrink-0
                          ${
                            isActive
                              ? 'bg-primary-100 text-primary-700'
                              : hasAccess
                              ? 'bg-gray-100 text-gray-700'
                              : 'bg-gray-100 text-gray-400'
                          }
                        `}
                      >
                        {module.icon}
                      </div>
                      <div className="flex-1 min-w-0 text-right">
                        <p
                          className={`
                            text-sm font-semibold truncate
                            ${
                              isActive
                                ? 'text-primary-900'
                                : hasAccess
                                ? 'text-gray-900'
                                : 'text-gray-600'
                            }
                          `}
                        >
                          {module.nameHe}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {module.name}
                        </p>
                      </div>
                    </div>

                    {/* Status Indicator */}
                    {!hasAccess ? (
                      <Lock className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    ) : isActive ? (
                      <div className="w-2 h-2 bg-primary-600 rounded-full flex-shrink-0" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    )}
                  </button>
                );
              })}
            </div>

            {/* Footer */}
            <div className="px-4 py-3 border-t border-gray-200 bg-gradient-to-br from-gray-50 to-white">
              <p className="text-xs text-gray-600 text-right">
                💡 <span className="font-semibold">טיפ:</span> השתמשו במעבר מהיר בין מודולים
                לניהול יעיל יותר
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Upsell Modal */}
      {upsellModule && (
        <ModuleUpsellModal
          isOpen={!!upsellModule}
          onClose={closeUpsellModal}
          moduleName={upsellModule.name}
          moduleNameHe={upsellModule.nameHe}
        />
      )}
    </>
  );
}
